import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../../../components/input";
import { useDispatch, useSelector } from "react-redux";
import { createServerSnapshot, listServer } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";
import Select from "../../../components/select";
import Button from "../../../components/button";
import PaymentModal from "../../../components/paymentModal";

const Snapshot = () => {
  
  document.title = "Take a snapshot - CloudVantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedProject = useSelector((state) => state.user.selectedProject);
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);
  const [fetchedServerDetails, setFetchedServerDetails] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");

  const selectedServer = useMemo(() => {
    return JSON.parse(sessionStorage.getItem("selectedServer")) || {};
  }, []);

  const handleGetServer = useCallback(
    async (serverId) => {
      const projectId = selectedProject?.id;

      if (!serverId || !projectId) {
        console.log("Missing serverId or projectId, skipping API call");
        setFetchedServerDetails(null);
        return;
      }

      const { payload } = await dispatch(listServer({ projectId, serverId }));

      if (payload && payload?.flavor?.disk !== undefined) {
        setFetchedServerDetails(payload);
      } else {
        setFetchedServerDetails(null);
      }
    },
    [dispatch, selectedProject]
  );

  const paymentOptions = [
    { value: "", label: "Select an option" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const calculateEstimatedCost = (diskSize, currency, paymentMethod) => {
    if (!diskSize || !paymentMethod) return { formatted: "0.00", numeric: 0 };

    const snapshotPricePerGB = currency === "NGN" ? 270 : 0.12;
    const diskSizeValue = parseFloat(diskSize);

    let totalCost = diskSizeValue * snapshotPricePerGB;

    if (paymentMethod === "yearly") {
      const yearlyPrice = totalCost * 12;
      const discount = yearlyPrice * 0.01; // 1% discount for yearly
      totalCost = yearlyPrice - discount;
    }

    return {
      formatted: totalCost.toLocaleString("en-US", {
        style: "currency",
        currency: currency === "NGN" ? "NGN" : "USD",
      }),
      numeric: totalCost,
    };
  };

  const createSnapshot = useFormik({
    initialValues: {
      snapshotName: "",
      payment: "",
      dewId: selectedServer?.serverId || "",
    },
    validationSchema: Yup.object({
      snapshotName: Yup.string().required("Snapshot name cannot be empty"),
      payment: Yup.string().required("Payment method is required"),
      dewId: Yup.string().required("Server ID is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { snapshotName, payment, dewId } = values;
      const estimate = calculateEstimatedCost(
        fetchedServerDetails?.flavor?.disk,
        currency,
        payment
      ).numeric;

      if (estimate > walletBalance) {
        setShowPaymentModal(true);
      } else {
        const snapshotData = {
          server_id: dewId,
          name: snapshotName,
          payment_method: payment,
          projectId: selectedProject.id,
        };

        const { payload } = await dispatch(
          createServerSnapshot({ snapshotData, navigate })
        );
        if (payload.statuscode === "00") {
          navigate("/manageServerSnapshots");
          resetForm();
        }
      }
    },
  });

  useEffect(() => {
    const serverId = selectedServer?.serverId;
    if (serverId && selectedProject) {
      handleGetServer(serverId);
    }
  }, [selectedServer?.serverId, selectedProject, handleGetServer]);

  useEffect(() => {
    const estimate = calculateEstimatedCost(
      fetchedServerDetails?.flavor?.disk,
      currency,
      createSnapshot.values.payment
    );
    setCalculatedEstimate(estimate.formatted);
  }, [fetchedServerDetails?.flavor?.disk, currency, createSnapshot.values.payment]);

  const diskSize = fetchedServerDetails?.flavor?.disk || 0;

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue mb-6">
        Take a snapshot for {fetchedServerDetails?.name || selectedServer?.serverName || "selected"} server (dew)
      </p>
      <p className="dark:text-gray-500 text-gray-400 text-sm mb-2 leading-loose md:leading-normal">
        A snapshot is a point-in-time backup of the entire virtual machine, including
        its data, system settings, and active processes. It allows for quick
        restoration in case of data loss or system issues, ensuring service
        continuity and security during updates or changes.
      </p>

      <div className="grid gap-4">
        <form onSubmit={createSnapshot.handleSubmit}>
          <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
            <Input
              type={"text"}
              label="Snapshot Name"
              name={"snapshotName"}
              onBlur={createSnapshot.handleBlur}
              onChange={createSnapshot.handleChange}
              placeholder={"Give your snapshot a name"}
              value={createSnapshot.values.snapshotName}
              onError={
                createSnapshot.errors.snapshotName &&
                createSnapshot.touched.snapshotName
                  ? createSnapshot.errors.snapshotName
                  : null
              }
            />
            <Input
              type={"text"}
              label="Server"
              name={"dewId"}
              value={fetchedServerDetails?.name || selectedServer?.serverName || ""}
              disabled={true}
              onError={
                createSnapshot.touched.dewId && createSnapshot.errors.dewId
                  ? createSnapshot.errors.dewId
                  : null
              }
            />
            <Select
              label="Payment Method"
              name="payment"
              options={paymentOptions}
              valueKey={"value"}
              labelKey={"label"}
              selectValue={createSnapshot.values.payment}
              selectBlur={createSnapshot.handleBlur}
              onChange={(event) => createSnapshot.setFieldValue("payment", event.target.value)}
              onError={
                createSnapshot.touched.payment && createSnapshot.errors.payment
                  ? createSnapshot.errors.payment
                  : null
              }
            />
            <div className="flex justify-between items-center gap-2">
              <div>
                <div className="text-xs">Estimated cost:</div>
                <div className="font-semibold crimson-font text-primaryBlue text-lg">
                  {calculatedEstimate}
                </div>
              </div>
              <Button
                buttonText={
                  calculateEstimatedCost(
                    fetchedServerDetails?.flavor?.disk,
                    currency,
                    createSnapshot.values.payment
                  ).numeric > walletBalance
                    ? "Pay Now"
                    : "Take Snapshot"
                }
                buttonRole={"submit"}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(
          fetchedServerDetails?.flavor?.disk,
          currency,
          createSnapshot.values.payment
        ).numeric}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createSnapshot.handleSubmit()}
      />
    </div>
  );
};

export default Snapshot;